<template>
  <div v-if="visible" class="modal-overlay">
    <div class="modal confirm-modal" :class="type">
      <div class="modal-icon" v-if="type === 'danger'">
        <AppIcon name="exclamation-triangle" />
      </div>
      <div class="modal-icon" v-else-if="type === 'warning'">
        <AppIcon name="exclamation-circle" />
      </div>
      <div class="modal-icon" v-else>
        <AppIcon name="info-circle" />
      </div>
      
      <h2>{{ title }}</h2>
      <p class="modal-message">{{ message }}</p>
      
      <div class="modal-actions">
        <button v-if="showCancel" class="btn-secondary" @click="onCancel" :disabled="loading">
          {{ cancelText }}
        </button>
        <button 
          :class="['btn-primary', { 'btn-danger': type === 'danger' }]" 
          @click="onConfirm"
          :disabled="loading"
        >
          <AppIcon v-if="loading" name="spinner" spin />
          {{ loading ? loadingText : confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ConfirmModal',
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: 'Confirm' },
    message: { type: String, default: 'Are you sure?' },
    confirmText: { type: String, default: 'OK' },
    cancelText: { type: String, default: 'Cancel' },
    loadingText: { type: String, default: 'Processing...' },
    showCancel: { type: Boolean, default: true },
    type: { type: String, default: 'default' },
    loading: { type: Boolean, default: false }
  },
  data() {
    return {
      escapeHandler: null
    }
  },
  methods: {
    onConfirm() { this.$emit('confirm') },
    onCancel() { if (!this.loading) this.$emit('cancel') }
  },
  mounted() {
    this.escapeHandler = (e) => {
      if (e.key === 'Escape' && this.visible && !this.loading) this.onCancel()
    }
    document.addEventListener('keydown', this.escapeHandler)
  },
  beforeUnmount() {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler)
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.confirm-modal {
  background: var(--white);
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 400px;
  text-align: center;
  box-shadow: var(--shadow-lg);
}

.modal-icon {
  width: 48px; height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 24px;
}

.confirm-modal.default .modal-icon { background: #dbeafe; color: #2563eb; }
.confirm-modal.warning .modal-icon { background: #fef3c7; color: #d97706; }
.confirm-modal.danger .modal-icon { background: #fee2e2; color: #dc2626; }

.confirm-modal h2 {
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--gray-900);
}

.modal-message {
  margin: 0 0 24px;
  color: var(--gray-600);
  font-size: 14px;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-secondary, .btn-primary {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-secondary {
  background: var(--white);
  border: 1px solid var(--gray-300);
  color: var(--gray-700);
}

.btn-secondary:hover:not(:disabled) { background: var(--gray-50); }

.btn-primary {
  background: var(--gray-900);
  border: 1px solid var(--gray-900);
  color: var(--white);
}

.btn-primary:hover:not(:disabled) { background: var(--gray-800); }
.btn-primary.btn-danger { background: #dc2626; border-color: #dc2626; }
.btn-primary.btn-danger:hover:not(:disabled) { background: #b91c1c; }

.btn-secondary:disabled, .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
